import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Check, Sparkles, Crown, Wine, Coffee, ChefHat, ArrowRight, Zap,
  Menu, X, LogIn, User, BarChart3, Shield, Clock, Users, Euro,
  ClipboardCheck, FileText, Building2, Star, Play, UtensilsCrossed,
  Beer, Hotel, Sprout
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Login Dialog
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Demo Selection Dialog
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoLoading, setDemoLoading] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await axios.get(`${API}/api/subscriptions/plans`);
      setPlans(res.data.plans.filter(p => p.id !== 'free'));
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      await login(loginForm.username, loginForm.password);
      setLoginDialogOpen(false);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (user) {
      // Already logged in, go to checkout
      setProcessingPlan(planId);
      try {
        const res = await axios.post(`${API}/api/payments/create-checkout`, {
          plan_id: planId,
          origin_url: window.location.origin
        }, { withCredentials: true });
        window.location.href = res.data.checkout_url;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Fehler beim Erstellen der Checkout-Session");
        setProcessingPlan(null);
      }
    } else {
      // Show login dialog
      setLoginDialogOpen(true);
    }
  };

  const startDemo = async (demoType = "cocktailbar") => {
    setDemoLoading(demoType);
    try {
      const res = await axios.post(`${API}/api/demo/start?demo_type=${demoType}`, {}, { withCredentials: true });
      if (res.data.success) {
        setDemoDialogOpen(false);
        const demoNames = {
          cocktailbar: "Cocktailbar",
          pizzeria: "Pizzeria Bella Italia",
          cafe: "Café Sonnenschein",
          biergarten: "Biergarten zur Linde",
          hotel: "Hotel Lindenhof",
          irish_pub_de: "The Shamrock Irish Pub (DE)",
          irish_pub_at: "The Shamrock Irish Pub (AT)",
          vegan_de: "Bowl & Soul Berlin (DE)",
          vegan_at: "Bowl & Soul Wien (AT)"
        };
        toast.success(`${demoNames[demoType] || demoType} Demo gestartet!`);
        window.location.href = "/dashboard";
      }
    } catch (err) {
      toast.error("Demo konnte nicht gestartet werden");
    } finally {
      setDemoLoading(null);
    }
  };
  
  const openDemoSelection = () => {
    setDemoDialogOpen(true);
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'getraenke': return <Wine className="w-8 h-8" />;
      case 'cafe': return <Coffee className="w-8 h-8" />;
      case 'restaurant': return <ChefHat className="w-8 h-8" />;
      default: return <Sparkles className="w-8 h-8" />;
    }
  };

  const getPlanColor = (planId) => {
    switch (planId) {
      case 'getraenke': return { bg: 'from-blue-500 to-blue-600', text: 'text-blue-500' };
      case 'cafe': return { bg: 'from-amber-500 to-orange-500', text: 'text-amber-500' };
      case 'restaurant': return { bg: 'from-violet-500 to-purple-600', text: 'text-violet-500' };
      default: return { bg: 'from-slate-500 to-slate-600', text: 'text-slate-500' };
    }
  };

  const features = [
    { icon: <BarChart3 />, title: "Echtzeit-Kalkulation", desc: "Sofortige Marge-Berechnung für alle Produkte" },
    { icon: <ClipboardCheck />, title: "HACCP & Hygiene", desc: "Alle Gesundheitsamt-Anforderungen erfüllt" },
    { icon: <Users />, title: "Mitarbeiter-System", desc: "Aufgaben, Checklisten & Zugangskontrolle" },
    { icon: <Euro />, title: "Kostenübersicht", desc: "Personal, Fixkosten & Wareneinsatz im Blick" },
    { icon: <FileText />, title: "Dokumentation", desc: "Lückenlose Nachverfolgung & Protokolle" },
    { icon: <Clock />, title: "Schichtplanung", desc: "Einfache Personalplanung & Zeiterfassung" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="landing-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-lg border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MargeVio" className="h-8 w-auto" />
              <span className="text-xl font-bold text-white">MargeVio</span>
              <span className="hidden sm:inline-flex items-center gap-1 bg-violet-500/20 text-violet-300 text-xs px-2 py-0.5 rounded-full">
                <Sparkles size={10} /> Beta
              </span>
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-6">
              <a href="#features" className="text-sm text-slate-300 hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="text-sm text-slate-300 hover:text-white transition-colors">Preise</a>
              <Button
                variant="ghost"
                size="sm"
                onClick={openDemoSelection}
                className="text-slate-300 hover:text-white gap-2"
              >
                <Play size={14} /> Demo
              </Button>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center gap-3">
              {user ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white gap-2"
                >
                  <User size={16} /> Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setLoginDialogOpen(true)}
                    className="hidden sm:flex text-slate-300 hover:text-white gap-2"
                    data-testid="header-login-btn"
                  >
                    <LogIn size={16} /> Anmelden
                  </Button>
                  <Button
                    onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                    className="bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white"
                  >
                    Jetzt starten
                  </Button>
                </>
              )}

              {/* Mobile Menu Button */}
              <button
                className="md:hidden p-2 text-slate-300 hover:text-white"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-800 border-t border-slate-700">
            <div className="px-4 py-3 space-y-3">
              <a href="#features" className="block text-slate-300 hover:text-white py-2">Features</a>
              <a href="#pricing" className="block text-slate-300 hover:text-white py-2">Preise</a>
              <Button variant="ghost" onClick={openDemoSelection} className="w-full justify-start gap-2">
                <Play size={14} /> Demo testen
              </Button>
              {!user && (
                <Button variant="outline" onClick={() => setLoginDialogOpen(true)} className="w-full gap-2">
                  <LogIn size={16} /> Anmelden
                </Button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-6">
            <Shield size={14} className="text-emerald-400" />
            <span className="text-sm text-emerald-300 font-medium">DSGVO-konform & Made in Germany</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Deine Gastronomie.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-blue-400">
              Perfekt kalkuliert.
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-8">
            Wareneinsatz, Margen, Mitarbeiter, HACCP & mehr – alles in einer App.
            Für Bars, Cafés und Restaurants.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={openDemoSelection}
              size="lg"
              className="w-full sm:w-auto bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white px-8 py-6 text-lg gap-2"
              data-testid="hero-demo-btn"
            >
              <Play size={20} /> Kostenlos testen
            </Button>
            <Button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              size="lg"
              className="w-full sm:w-auto border-slate-600 text-slate-300 hover:bg-slate-800 px-8 py-6 text-lg"
            >
              Preise ansehen
            </Button>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500">
            <span className="flex items-center gap-2"><Check size={16} className="text-emerald-400" /> Keine Kreditkarte nötig</span>
            <span className="flex items-center gap-2"><Check size={16} className="text-emerald-400" /> 7 Tage kostenlos</span>
            <span className="flex items-center gap-2"><Check size={16} className="text-emerald-400" /> Jederzeit kündbar</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Alles was du brauchst</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Von der Kalkulation bis zur Dokumentation – MargeVio deckt alle Bereiche ab.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div key={i} className="bg-slate-800/80 border border-slate-700/50 rounded-xl p-6 hover:border-violet-500/50 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center text-violet-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/30 rounded-full px-4 py-1.5 mb-6">
              <Zap size={16} className="text-violet-400" />
              <span className="text-sm text-violet-300 font-medium">Bis zu 50% Rabatt – Limitiertes Angebot</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Wähle deinen Plan
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Finde den passenden Plan für dein Business. Alle Pläne monatlich kündbar.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {plans.map((plan) => {
              const colors = getPlanColor(plan.id);
              const isPopular = plan.id === 'cafe';
              
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                    isPopular ? 'ring-2 ring-amber-500 shadow-xl shadow-amber-500/20' : ''
                  }`}
                  data-testid={`plan-card-${plan.id}`}
                >
                  {isPopular && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-center text-sm font-semibold py-2">
                      <Crown size={14} className="inline mr-1" /> BELIEBTESTER PLAN
                    </div>
                  )}

                  <div className={`bg-slate-800/90 backdrop-blur-sm p-6 lg:p-8 h-full flex flex-col ${isPopular ? 'pt-14' : ''}`}>
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center text-white mb-4`}>
                      {getPlanIcon(plan.id)}
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-1">{plan.display_name}</h3>
                    <p className="text-sm text-slate-400 mb-4">{plan.target_audience}</p>

                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold text-white">{plan.price.toFixed(2).replace('.', ',')}€</span>
                        <span className="text-slate-500">/Monat</span>
                      </div>
                      {plan.original_price > plan.price && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-slate-500 line-through">{plan.original_price.toFixed(2).replace('.', ',')}€</span>
                          <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full font-semibold">
                            -{plan.discount_percent}%
                          </span>
                        </div>
                      )}
                    </div>

                    <ul className="space-y-3 mb-8 flex-grow">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm">
                          <Check size={18} className={`${colors.text} shrink-0 mt-0.5`} />
                          <span className="text-slate-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      onClick={() => handleSubscribe(plan.id)}
                      disabled={processingPlan === plan.id}
                      className={`w-full py-6 text-base font-semibold bg-gradient-to-r ${colors.bg} hover:opacity-90 text-white`}
                      data-testid={`subscribe-${plan.id}`}
                    >
                      {processingPlan === plan.id ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Wird verarbeitet...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Jetzt starten <ArrowRight size={18} />
                        </span>
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Guarantee */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-slate-800/50 border border-slate-700 rounded-full px-6 py-3">
              <Shield size={20} className="text-emerald-400" />
              <span className="text-slate-300">14 Tage Geld-zurück-Garantie</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-violet-600/20 to-purple-600/20 border-y border-violet-500/20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Bereit durchzustarten?
          </h2>
          <p className="text-slate-400 mb-8">
            Teste MargeVio 7 Tage kostenlos. Keine Kreditkarte erforderlich.
          </p>
          <Button
            onClick={openDemoSelection}
            size="lg"
            className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-lg font-semibold gap-2"
          >
            <Play size={20} /> Kostenlose Demo starten
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-800">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="MargeVio" className="h-8 w-auto" />
              <span className="text-lg font-bold text-white">MargeVio</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>© 2025 MargeVio</span>
              <span>by Gian-Luca</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Demo Selection Dialog */}
      <Dialog open={demoDialogOpen} onOpenChange={setDemoDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2 text-xl">
              <Play size={22} className="text-violet-400" />
              Demo-Modus wählen
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Wähle eine realistische Branchenumgebung zum Testen
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-3 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Cocktailbar Option */}
            <button
              onClick={() => startDemo("cocktailbar")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-blue-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-cocktailbar"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white shrink-0">
                  <Wine size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Cocktailbar
                    {demoLoading === "cocktailbar" && (
                      <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Bars, Kneipen & Lounges</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">35+ Cocktails</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">4 Mitarbeiter</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-blue-400 transition-colors shrink-0" />
              </div>
            </button>
            
            {/* Pizzeria Option */}
            <button
              onClick={() => startDemo("pizzeria")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-orange-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-pizzeria"
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white shrink-0">
                  <UtensilsCrossed size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Pizzeria Bella Italia
                    {demoLoading === "pizzeria" && (
                      <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Italienisches Restaurant</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">14 Pizzas</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">7 Mitarbeiter</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-orange-400 transition-colors shrink-0" />
              </div>
            </button>
            
            {/* Café Option - NEU */}
            <button
              onClick={() => startDemo("cafe")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-amber-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-cafe"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">NEU</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center text-white shrink-0">
                  <Coffee size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Café Sonnenschein
                    {demoLoading === "cafe" && (
                      <div className="w-4 h-4 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Café & Konditorei</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Frühstück</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">30 Kaffees</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-amber-400 transition-colors shrink-0" />
              </div>
            </button>
            
            {/* Biergarten Option - NEU */}
            <button
              onClick={() => startDemo("biergarten")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-yellow-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-biergarten"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-yellow-500 to-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">NEU</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 flex items-center justify-center text-white shrink-0">
                  <Beer size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Biergarten zur Linde
                    {demoLoading === "biergarten" && (
                      <div className="w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Bayerischer Biergarten</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">400 Plätze</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">9 Mitarbeiter</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-yellow-400 transition-colors shrink-0" />
              </div>
            </button>
            
            {/* Hotel Option - NEU */}
            <button
              onClick={() => startDemo("hotel")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-sky-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-hotel"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-sky-500 to-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">NEU</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center text-white shrink-0">
                  <Hotel size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Hotel Lindenhof
                    {demoLoading === "hotel" && (
                      <div className="w-4 h-4 border-2 border-sky-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Hotel Restaurant & Bar</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Restaurant</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">10 Mitarbeiter</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-sky-400 transition-colors shrink-0" />
              </div>
            </button>

            {/* Irish Pub DE */}
            <button
              onClick={() => startDemo("irish_pub_de")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-emerald-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-irish_pub_de"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">DE</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white shrink-0">
                  <Beer size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    The Shamrock (DE)
                    {demoLoading === "irish_pub_de" && (
                      <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Irish Pub · Deutschland (7/19% MwSt)</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Bier & Whiskey</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Live Music</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-emerald-400 transition-colors shrink-0" />
              </div>
            </button>

            {/* Irish Pub AT */}
            <button
              onClick={() => startDemo("irish_pub_at")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-teal-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-irish_pub_at"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">AT</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center text-white shrink-0">
                  <Beer size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    The Shamrock (AT)
                    {demoLoading === "irish_pub_at" && (
                      <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Irish Pub · Österreich (10/20% USt)</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Bier & Whiskey</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Kommunalsteuer</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-teal-400 transition-colors shrink-0" />
              </div>
            </button>

            {/* Vegan DE */}
            <button
              onClick={() => startDemo("vegan_de")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-lime-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-vegan_de"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-lime-500 to-green-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">DE · NEU</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lime-500 to-green-600 flex items-center justify-center text-white shrink-0">
                  <Sprout size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Bowl & Soul (DE)
                    {demoLoading === "vegan_de" && (
                      <div className="w-4 h-4 border-2 border-lime-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Veganes Franchise · Berlin · 3M-Forecast</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">26 Bowls</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Mate · Fritz</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Lagerlogistik</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-lime-400 transition-colors shrink-0" />
              </div>
            </button>

            {/* Vegan AT */}
            <button
              onClick={() => startDemo("vegan_at")}
              disabled={demoLoading !== null}
              className="relative group p-4 rounded-xl border border-slate-600 bg-slate-700/50 hover:border-emerald-500 hover:bg-slate-700 transition-all text-left"
              data-testid="demo-select-vegan_at"
            >
              <div className="absolute -top-2 -right-2 bg-gradient-to-r from-emerald-500 to-lime-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">AT · NEU</div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-lime-600 flex items-center justify-center text-white shrink-0">
                  <Sprout size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white mb-0.5 flex items-center gap-2">
                    Bowl & Soul (AT)
                    {demoLoading === "vegan_at" && (
                      <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </h3>
                  <p className="text-xs text-slate-400 mb-2">Veganes Franchise · Wien · 10/20% USt</p>
                  <div className="flex flex-wrap gap-1.5 text-xs">
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Bio Austria</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Kommunalsteuer</span>
                    <span className="bg-slate-600 text-slate-300 px-1.5 py-0.5 rounded">Lagerlogistik</span>
                  </div>
                </div>
                <ArrowRight size={18} className="text-slate-500 group-hover:text-emerald-400 transition-colors shrink-0" />
              </div>
            </button>
          </div>
          
          <p className="text-center text-xs text-slate-500 mt-4">
            Demo-Daten werden nach 24h automatisch gelöscht
          </p>
        </DialogContent>
      </Dialog>

      {/* Login Dialog */}
      <Dialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <LogIn size={20} className="text-violet-400" />
              Anmelden
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4 mt-2">
            <div>
              <Input
                type="text"
                placeholder="Benutzername"
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white placeholder-slate-400"
                data-testid="login-username-input"
              />
            </div>
            <div>
              <Input
                type="password"
                placeholder="Passwort"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="bg-slate-700 border-slate-600 text-white placeholder-slate-400"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loginLoading}
              className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white py-5"
              data-testid="login-submit"
            >
              {loginLoading ? "Wird angemeldet..." : "Anmelden"}
            </Button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setLoginDialogOpen(false); navigate("/login"); }}
                className="text-sm text-slate-400 hover:text-violet-400"
              >
                Passwort vergessen?
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
